/**@jsxImportSource theme-ui */
import React from 'react';
import { motion } from 'framer-motion';
import { ResponsiveStyleValue } from 'theme-ui';
import Container from '../Container';
import FlexContainer from '../FlexContainer';
import { Icon, IconButton } from '../Icon';
import { Overlay, OverlayContent, OverlayControlProps } from '../Overlay';
import { useBreakpointValue } from '../../hooks';
import { CarrotTheme } from '../../theme';
import { AllCaroteneColors } from '../utils/types';

type Placement = 'left' | 'right';

export interface DrawerProps extends OverlayControlProps {
    showClose?: boolean;
    fullScreenMobile?: boolean;
    width?: ResponsiveStyleValue<string>;
    placement?: Placement;
    background?: ResponsiveStyleValue<AllCaroteneColors>;
    closeButton?: React.ComponentType<unknown>;
}

type AnimationDefinition = {
    open: Record<string, unknown>;
    closed: Record<string, unknown>;
};

function DefaultDrawerHeader({ children }: React.PropsWithChildren<unknown>): JSX.Element {
    return (
        <FlexContainer paddingLeftRight="25px" sx={{ paddingTop: '25px' }} justifyContent="flex-end">
            {children}
        </FlexContainer>
    );
}

function DefaultDrawerCloseButton(props: React.HTMLAttributes<HTMLButtonElement>): JSX.Element {
    return (
        <DefaultDrawerHeader>
            <IconButton
                aria-label="Close Drawer"
                icon={<Icon size="18px" color="carrotOrange" name="x" />}
                {...props}
            />
        </DefaultDrawerHeader>
    );
}

function getLeftAnimations(width: string): AnimationDefinition {
    return {
        open: {
            x: 0,
        },
        closed: {
            x: `-${width}`,
        },
    };
}
function getRightAnimations(width: string): AnimationDefinition {
    return {
        open: {
            x: `calc(100% - ${width})`,
        },
        closed: {
            x: '100%',
        },
    };
}

function getAnimationPropsByPlacementAndWidth(placement: Placement, width: string) {
    switch (placement) {
        case 'left':
            return {
                variants: getLeftAnimations(width),
            };
        case 'right':
            return {
                variants: getRightAnimations(width),
            };
        default:
            return {
                variants: {},
            };
    }
}

type DrawerAnimationProps = React.PropsWithChildren<{
    placement: Placement;
    width: string;
}>;
function DrawerAnimationLayer(props: DrawerAnimationProps): JSX.Element {
    const { variants } = getAnimationPropsByPlacementAndWidth(props.placement, props.width as string);
    return (
        <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            transition={{ ease: [0.65, 0.05, 0.36, 1], duration: 0.2 }}
            variants={variants}
        >
            {props.children}
        </motion.div>
    );
}

export function Drawer({
    children,
    open,
    onClose,
    showClose,
    placement,
    width,
    isDismissable,
    background,
    closeButton,
}: React.PropsWithChildren<DrawerProps>): JSX.Element {
    const overlayRef = React.useRef();
    const responsiveWidth = useBreakpointValue<string>(width);
    const AnimationLayer = React.useMemo(() => {
        function Layer(props: DrawerAnimationProps): JSX.Element {
            return <DrawerAnimationLayer placement={placement} width={responsiveWidth} {...props} />;
        }
        Layer.displayName = 'AnimationLayer';
        return Layer;
    }, [placement, responsiveWidth]);
    return (
        <Overlay background="black30" open={open}>
            <OverlayContent
                animationLayer={AnimationLayer}
                sx={{
                    backgroundColor: background,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    overflowY: 'auto',
                    msOverflowStyle: 'none',
                    '::-webkit-scrollbar': {
                        display: 'none',
                    },
                }}
                as="aside"
                closeButton={showClose ? closeButton : null}
                maxWidth={responsiveWidth}
                width="100vw"
                maxHeight="100vh"
                height="100vh"
                ref={overlayRef}
                open={open}
                onClose={onClose}
                shadow="none"
                isDismissable={isDismissable}
            >
                <Container>{children}</Container>
            </OverlayContent>
        </Overlay>
    );
}

Drawer.defaultProps = {
    isDismissable: true,
    showClose: true,
    width: '300px',
    placement: 'left',
    background: (theme: CarrotTheme) => theme.colors.white,
    closeButton: DefaultDrawerCloseButton,
};
